import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Typography,
  Space,
  Empty,
  Spin,
  Popconfirm,
  message,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  MessageOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

interface Prompt {
  id: number;
  name: string;
  content: string;
  char_count: number;
  version_count: number;
  created_at: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

const PromptList: React.FC = () => {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      const response = await axios.get(`${API_URL}/prompts`);
      setPrompts(response.data);
    } catch (error) {
      console.error('Failed to fetch prompts:', error);
      message.error('Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await axios.delete(`${API_URL}/prompts/${id}`);
      setPrompts(prompts.filter(p => p.id !== id));
      message.success('Prompt deleted successfully');
    } catch (error) {
      console.error('Failed to delete prompt:', error);
      message.error('Failed to delete prompt');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0 }}>
          <MessageOutlined style={{ marginRight: '12px', color: '#6366f1' }} />
          Prompts
        </Title>
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          onClick={() => navigate('/prompts/new')}
        >
          Create Prompt
        </Button>
      </div>

      {prompts.length === 0 ? (
        <Card>
          <Empty
            image={<FileTextOutlined style={{ fontSize: '64px', color: '#ccc' }} />}
            description={
              <Space direction="vertical" size="small">
                <Text strong style={{ fontSize: '16px' }}>No prompts yet</Text>
                <Text type="secondary">Create your first prompt to get started.</Text>
              </Space>
            }
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/prompts/new')}>
              Create Prompt
            </Button>
          </Empty>
        </Card>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {prompts.map(prompt => (
            <Card
              key={prompt.id}
              hoverable
              onClick={() => navigate(`/prompts/${prompt.id}`)}
              style={{ cursor: 'pointer' }}
              title={
                <Space>
                  <MessageOutlined style={{ color: '#6366f1' }} />
                  {prompt.name}
                </Space>
              }
              extra={
                <Space onClick={(e) => e.stopPropagation()}>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/prompts/${prompt.id}`);
                    }}
                  >
                    Edit
                  </Button>
                  <Popconfirm
                    title="Delete prompt"
                    description="Are you sure you want to delete this prompt?"
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      handleDelete(prompt.id);
                    }}
                    onCancel={(e) => e?.stopPropagation()}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()}>
                      Delete
                    </Button>
                  </Popconfirm>
                </Space>
              }
            >
              <Space size="large">
                <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                  {(prompt.char_count || 0).toLocaleString()} characters
                </Tag>
                <Tag color="purple" style={{ fontSize: '14px', padding: '4px 12px' }}>
                  {prompt.version_count || 1} version{(prompt.version_count || 1) !== 1 ? 's' : ''}
                </Tag>
                <Text type="secondary">
                  Created: {formatDate(prompt.created_at)}
                </Text>
              </Space>
            </Card>
          ))}
        </Space>
      )}
    </div>
  );
};

export default PromptList;
